import { useState, useEffect } from 'react'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import {
  DollarSign,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  FileText,
  Search,
  Filter,
  Info
} from 'lucide-react'

interface LoanRequest {
  id: string
  amount: number
  purpose: string
  business_plan: string | null
  status: 'pending' | 'approved' | 'rejected'
  admin_notes: string | null
  created_at: string
  updated_at: string
  approved_amount: number | null
  repayment_terms: string | null
  rejection_reason: string | null
}

export function LoanHistory() {
  const user = useAuthStore((state) => state.user)
  const [loans, setLoans] = useState<LoanRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [selectedLoan, setSelectedLoan] = useState<LoanRequest | null>(null)
  const [showLoanDetails, setShowLoanDetails] = useState(false)

  useEffect(() => {
    if (user) {
      fetchLoanRequests()
    }
  }, [user])

  const fetchLoanRequests = async () => {
    if (!user) return

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('loan_requests')
        .select('*')
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setLoans(data || [])
    } catch (err) {
      console.error('Error fetching loan requests:', err)
      setToastMessage('Erreur lors du chargement des demandes de prêt')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" />
            En attente
          </span>
        )
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approuvée
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Refusée
          </span>
        )
      default:
        return null
    }
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Loan Details Modal */}
      {showLoanDetails && selectedLoan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Détails de la Demande de Prêt
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Soumise le {formatDate(selectedLoan.created_at)}
                </p>
              </div>
              <button
                onClick={() => setShowLoanDetails(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Loan Status */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">Statut:</div>
                <div>{getStatusBadge(selectedLoan.status)}</div>
              </div>

              {/* Loan Amount */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium text-gray-500">Montant demandé:</div>
                  <div className="text-lg font-bold text-gray-900">{formatAmount(selectedLoan.amount)}</div>
                </div>
                
                {selectedLoan.status === 'approved' && selectedLoan.approved_amount && (
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200">
                    <div className="text-sm font-medium text-gray-500">Montant approuvé:</div>
                    <div className="text-lg font-bold text-green-600">{formatAmount(selectedLoan.approved_amount)}</div>
                  </div>
                )}
              </div>

              {/* Loan Purpose */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Objectif du prêt</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedLoan.purpose}</p>
                </div>
              </div>

              {/* Business Plan */}
              {selectedLoan.business_plan && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Plan d'affaires</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedLoan.business_plan}</p>
                  </div>
                </div>
              )}

              {/* Admin Response */}
              {selectedLoan.status === 'approved' && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="text-sm font-medium text-green-800 flex items-center mb-2">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Demande Approuvée
                  </h4>
                  
                  {selectedLoan.repayment_terms && (
                    <div className="mt-2">
                      <h5 className="text-xs font-medium text-green-800">Conditions de remboursement:</h5>
                      <p className="text-sm text-green-700 mt-1">{selectedLoan.repayment_terms}</p>
                    </div>
                  )}
                  
                  {selectedLoan.admin_notes && (
                    <div className="mt-2">
                      <h5 className="text-xs font-medium text-green-800">Notes:</h5>
                      <p className="text-sm text-green-700 mt-1">{selectedLoan.admin_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {selectedLoan.status === 'rejected' && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h4 className="text-sm font-medium text-red-800 flex items-center mb-2">
                    <XCircle className="h-4 w-4 mr-2" />
                    Demande Refusée
                  </h4>
                  
                  {selectedLoan.rejection_reason && (
                    <div className="mt-2">
                      <h5 className="text-xs font-medium text-red-800">Raison du refus:</h5>
                      <p className="text-sm text-red-700 mt-1">{selectedLoan.rejection_reason}</p>
                    </div>
                  )}
                  
                  {selectedLoan.admin_notes && (
                    <div className="mt-2">
                      <h5 className="text-xs font-medium text-red-800">Notes:</h5>
                      <p className="text-sm text-red-700 mt-1">{selectedLoan.admin_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {selectedLoan.status === 'pending' && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-yellow-500 mr-2" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800">En attente d'examen</h4>
                      <p className="text-xs text-yellow-700 mt-1">
                        Votre demande est en cours d'examen par notre équipe. Nous vous contacterons dès qu'une décision sera prise.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4 flex justify-end">
                <Button
                  variant="secondary"
                  onClick={() => setShowLoanDetails(false)}
                >
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            Mes Demandes de Prêt
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : loans.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune demande de prêt</h3>
            <p className="mt-1 text-sm text-gray-500">
              Vous n'avez pas encore fait de demande de prêt
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Mobile view */}
            <div className="md:hidden space-y-4">
              {loans.map((loan) => (
                <div 
                  key={loan.id} 
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedLoan(loan)
                    setShowLoanDetails(true)
                  }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <DollarSign className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-lg font-medium text-gray-900">{formatAmount(loan.amount)}</span>
                    </div>
                    {getStatusBadge(loan.status)}
                  </div>
                  <div className="text-sm text-gray-500 mb-2">
                    Demandé le {formatDate(loan.created_at)}
                  </div>
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {loan.purpose}
                    </p>
                  </div>
                  <div className="mt-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedLoan(loan)
                        setShowLoanDetails(true)
                      }}
                    >
                      Détails
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Desktop view */}
            <div className="hidden md:block">
              {loans.map((loan) => (
                <div 
                  key={loan.id} 
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedLoan(loan)
                    setShowLoanDetails(true)
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="mb-2 sm:mb-0">
                      <div className="flex items-center">
                        <DollarSign className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-lg font-medium text-gray-900">{formatAmount(loan.amount)}</span>
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        Demandé le {formatDate(loan.created_at)}
                      </div>
                    </div>
                    <div className="flex items-center">
                      {getStatusBadge(loan.status)}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedLoan(loan)
                          setShowLoanDetails(true)
                        }}
                      >
                        Détails
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 line-clamp-1">
                      {loan.purpose}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}